import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Radio, Select, Spin, TreeSelect } from 'antd';
import './Profile.scss';
import { useDispatch } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { useSelector } from 'react-redux';
import { clearState, selectAllSettings } from '../../../modules/settings/store/slice';
import { doCreateSetting, doGetSettings, doUpdateSetting } from '../../../modules/settings/store/thunk';
import { ROLES } from '../../../constants';

const Labels = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const role = useSelector((state) => state.auth.user.role);

  const existingSettings = useSelector((state) => selectAllSettings(state)).flat();
  const isFetching = useSelector((state) => state.setting.isFetching);
  const companies = useSelector((state) => state.auth.user.companies || [state.auth.user.company]);

  const [companyId, setCompanyId] = useState(null);

  const onChange = (v, props) => {
    if (!props.type) {
      return;
    }
    switch (props.type) {
      case 'company':
        setCompanyId(v);
        break;
      default:
        break;
    }
  };

  const filteredSettings = useMemo(() => {
    return existingSettings.filter((setting) => {
      if (companyId) {
        return setting.companyId === companyId;
      }
      return false;
    });
  }, [existingSettings, companyId]);

  const options = useMemo(() => {
    let res = [];

    if (['COMPANY_OWNER', 'ADMIN'].includes(role)) {
      res.push({
        value: 'companies',
        title: 'Companies',
        children: [
          ...companies.map((company) => ({
            label: company.name,
            value: company.id,
            type: 'company',
          })),
        ],
      });
    }
    return res;
  }, [companies, role]);

  useEffect(() => {
    if (role === ROLES.ADMIN) {
      dispatch(doGetSettings({ category: 'labels' }));
    } else if (role === ROLES.COMPANY_OWNER && companyId) {
      dispatch(doGetSettings({ category: 'labels', companyId }));
    }
    return () => dispatch(clearState()); // explicitly clear the state to prevent it from being used in different component
  }, [companyId]);

  useEffect(() => {
    form.setFieldsValue({
      ORIENTATION: filteredSettings.find((setting) => setting.keyword === 'ORIENTATION')?.value || 'landscape',
      PRINT_SIZE: filteredSettings.find((setting) => setting.keyword === 'PRINT_SIZE')?.value || 'A6',
    });
  }, [filteredSettings, form]);

  const onSubmit = async (values) => {
    const updates = Object.keys(values).map((key) => {
      const existingSetting = filteredSettings.find((e) => key === e.keyword);
      return {
        id: existingSetting?.id || null,
        keyword: key,
        value: values[key],
        group: 'LABELS',
        companyId: companyId ? companyId : null,
      };
    });

    await Promise.all(
      updates.map(async (u) => {
        if (u.id && u.value) {
          // if it existed and has a value => update it & refetch
          await dispatch(doUpdateSetting(u));
          await dispatch(doGetSettings({ category: 'labels', companyId }));
        } else if (u.value) {
          // if it didn't exist => create it & refetch
          await dispatch(doCreateSetting(u));
          await dispatch(doGetSettings({ category: 'labels', companyId }));
        }
      }),
    );
  };

  const onOkLabels = () => {
    form.submit();
  };
  return (
    <Spin spinning={isFetching}>
      <div className="info">
        <div className="info-content">
          <h3>Labels</h3>
          <h5>Specify your preferred label size and orientation</h5>

          <h4>Please select the account, company or location you want to apply settings to:</h4>
          <TreeSelect
            placeholder="Please select"
            className="select-small"
            treeData={options}
            treeDefaultExpandAll
            onSelect={onChange}
            style={{ minWidth: '200px' }}
          />
        </div>
        {companyId && (
          <div className="UserForm">
            <Form form={form} onFinish={onSubmit} layout="vertical" scrollToFirstError className="FormContent">
              <Form.Item label="Orientation" name="ORIENTATION">
                <Radio.Group
                  options={[
                    { label: 'Landscape', value: 'landscape' },
                    { label: 'Portrait', value: 'portrait' },
                  ]}
                />
              </Form.Item>
              <Form.Item label="Print Size" name="PRINT_SIZE">
                <Select
                  options={[
                    { label: 'A6', value: 'A6' },
                    { label: 'A4', value: 'A4' },
                  ]}
                />
              </Form.Item>
            </Form>
            <div className="FormFooter">
              <Button
                className="CreateButton ButtonPrimary"
                type="primary"
                style={{ marginLeft: 'auto' }}
                onClick={onOkLabels}
              >
                Update
                <ReactSVG className="icon" src="/icons/done.svg" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
};

export default Labels;
